import { useActiveStep } from '@/layouts/AutomationPageWrapper/hooks/useActiveStep.ts';
import SidebarStepCard from '@/pages/Editor/components/StepSidebar/SidebarStepCard.tsx';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import { useMemo } from 'react';
import { Stack } from '@mantine/core';
import CustomSpinner from '@/components/Spinner.tsx';
import DataSourceSidebar from '@/pages/Editor/components/StepSidebar/DataSourceSidebar.tsx';
import useRetrieveAutomation from '@/hooks/api/automations/useRetrieveAutomation.ts';
import { findStepById } from '@/utils/stepUtils.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import LocalSubflowSidebar from '@/pages/Editor/components/LocalSubflows/LocalSubflowSidebar.tsx';
import {
  findLocalSubflowStep,
  isNestedLocalSubflowStep,
} from '@/pages/Editor/components/LocalSubflows/localSubflowStepUtils.ts';
import { AutomationStep } from '@/types/automationSteps.ts';
import useRetrieveEditorCompositionCapabilities from '@/hooks/api/automations/useRetrieveEditorCompositionCapabilities.ts';

const StepSidebar = () => {
  const { stepId, setActiveStep } = useActiveStep();
  const { target } = useActiveCompositionTarget();

  const { data: version } = useRetrieveLatestAutomationVersion();

  const { data: automation } = useRetrieveAutomation();
  const localSubflowId = target.scope_kind === 'local_subflow' ? target.subflow_id : undefined;
  const localSubflow = useMemo(
    () => version?.local_subflows?.find((subflow) => subflow.subflow_id === localSubflowId),
    [localSubflowId, version?.local_subflows],
  );
  const { data: moduleCapabilities, isLoading: moduleCapabilitiesLoading } = useRetrieveEditorCompositionCapabilities(
    localSubflow?.subflow_id,
    target.scope_kind === 'local_subflow' ? target.step_id : undefined,
  );

  const step = useMemo(() => {
    if (target.scope_kind === 'local_subflow') {
      if (!target.step_id || !localSubflow) return null;
      return findLocalSubflowStep(localSubflow.steps, target.step_id) as AutomationStep | null;
    }
    if (!stepId || !version?.steps) return null;
    const foundStep = findStepById(version.steps, stepId) ?? null;
    if (!foundStep) {
      setActiveStep(undefined);
    }
    return foundStep;
  }, [localSubflow, setActiveStep, stepId, target.scope_kind, target.step_id, version?.steps]);

  const hasParentStep = useMemo(() => {
    if (target.scope_kind === 'local_subflow') {
      return Boolean(
        localSubflow
        && target.step_id
        && isNestedLocalSubflowStep(localSubflow.steps, target.step_id),
      );
    }
    return !version?.steps.find((s) => s.step_id === stepId);
  }, [localSubflow, stepId, target.scope_kind, target.step_id, version?.steps]);

  if (!automation || !version || (target.scope_kind === 'root' && stepId && !step)) {
    return (
      <Stack h={'100%'} w={'100%'}>
        <CustomSpinner />
      </Stack>
    );
  } else if (target.scope_kind === 'local_subflow') {
    if (!localSubflow) {
      return (
        <DataSourceSidebar
          automation={automation}
          modelSettings={version.model_settings}
          schemas={version.schemas}
        />
      );
    }
    return step
      ? <SidebarStepCard
          step={step}
          key={`${localSubflowId}:${step.step_id}`}
          hasParentStep={hasParentStep}
          moduleCapabilities={moduleCapabilities}
          moduleCapabilitiesLoading={moduleCapabilitiesLoading}
          scopedSteps={localSubflow?.steps as AutomationStep[] | undefined}
        />
      : <LocalSubflowSidebar subflow={localSubflow} stepId={target.step_id} />;
  } else if (stepId && step) {
    return <SidebarStepCard step={step} key={step.step_id} hasParentStep={hasParentStep} />;
  } else {
    return (
      <DataSourceSidebar
        automation={automation}
        modelSettings={version.model_settings}
        schemas={version.schemas}
      />
    );
  }
};

export default StepSidebar;
